/**
 * Touch Gesture Utilities
 * Provides touch and gesture support for modern mobile interfaces
 */

/**
 * Touch Gesture Manager
 * Handles swipe, drag, pull-to-refresh, and other touch gestures
 */
export class TouchGestureManager {
  constructor() {
    this.touchStartX = 0;
    this.touchStartY = 0;
    this.touchEndX = 0;
    this.touchEndY = 0;
    this.minSwipeDistance = 50;
    this.maxSwipeTime = 300;
    this.touchStartTime = 0;
    
    // Haptic feedback simulation
    this.hapticEnabled = 'vibrate' in navigator;
    
    this.init();
  }

  /**
   * Initialize touch gesture handling
   */
  init() {
    this.setupSwipeHandlers();
    this.setupPullToRefresh();
    this.setupDragDrop();
    this.setupHapticFeedback();
  }

  /**
   * Setup swipe gesture handlers
   */
  setupSwipeHandlers() {
    document.addEventListener('touchstart', (e) => {
      this.handleTouchStart(e);
    }, { passive: true });

    document.addEventListener('touchmove', (e) => {
      this.handleTouchMove(e);
    }, { passive: false });

    document.addEventListener('touchend', (e) => {
      this.handleTouchEnd(e);
    }, { passive: true });
  }

  /**
   * Handle touch start
   */
  handleTouchStart(e) {
    const touch = e.touches[0];
    this.touchStartX = touch.clientX;
    this.touchStartY = touch.clientY;
    this.touchStartTime = Date.now();
  }

  /**
   * Handle touch move
   */
  handleTouchMove(e) {
    const touch = e.touches[0];
    this.touchEndX = touch.clientX;
    this.touchEndY = touch.clientY;

    // Prevent scroll for swipeable elements
    const target = e.target.closest('.swipeable');
    if (target) {
      const deltaX = Math.abs(this.touchEndX - this.touchStartX);
      const deltaY = Math.abs(this.touchEndY - this.touchStartY);
      
      // If horizontal swipe is greater than vertical, prevent scroll
      if (deltaX > deltaY && deltaX > 10) {
        e.preventDefault();
      }
    }
  }

  /**
   * Handle touch end and detect swipe gestures
   */
  handleTouchEnd(e) {
    const touchDuration = Date.now() - this.touchStartTime;
    
    if (touchDuration > this.maxSwipeTime) return;

    const deltaX = this.touchEndX - this.touchStartX;
    const deltaY = this.touchEndY - this.touchStartY;
    const absDeltaX = Math.abs(deltaX);
    const absDeltaY = Math.abs(deltaY);

    // Determine swipe direction
    if (absDeltaX > this.minSwipeDistance && absDeltaX > absDeltaY) {
      const direction = deltaX > 0 ? 'right' : 'left';
      this.handleSwipe(e.target, direction, deltaX);
    } else if (absDeltaY > this.minSwipeDistance && absDeltaY > absDeltaX) {
      const direction = deltaY > 0 ? 'down' : 'up';
      this.handleSwipe(e.target, direction, deltaY);
    }
  }

  /**
   * Handle swipe gestures
   */
  handleSwipe(target, direction, distance) {
    const swipeableElement = target.closest('.swipeable');
    
    if (!swipeableElement) return;

    // Trigger haptic feedback
    this.triggerHaptic('light');

    // Handle swipe delete
    if (swipeableElement.classList.contains('swipe-delete')) {
      this.handleSwipeDelete(swipeableElement, direction);
    }

    // Handle swipe carousel
    if (swipeableElement.classList.contains('swipe-carousel')) {
      this.handleSwipeCarousel(swipeableElement, direction);
    }

    // Dispatch custom swipe event
    const swipeEvent = new CustomEvent('swipe', {
      detail: {
        direction,
        distance,
        element: swipeableElement
      }
    });
    swipeableElement.dispatchEvent(swipeEvent);
  }

  /**
   * Handle swipe to delete functionality
   */
  handleSwipeDelete(element, direction) {
    if (direction === 'left') {
      element.classList.add('swiped-left');
      
      // Show delete actions
      const deleteActions = element.querySelector('.swipe-actions');
      if (deleteActions) {
        deleteActions.style.transform = 'translateX(0)';
        deleteActions.style.opacity = '1';
      }

      // Auto-hide after 3 seconds
      setTimeout(() => {
        this.resetSwipeDelete(element);
      }, 3000);
    } else if (direction === 'right') {
      this.resetSwipeDelete(element);
    }
  }

  /**
   * Reset swipe delete state
   */
  resetSwipeDelete(element) {
    element.classList.remove('swiped-left');
    const deleteActions = element.querySelector('.swipe-actions');
    if (deleteActions) {
      deleteActions.style.transform = 'translateX(100%)';
      deleteActions.style.opacity = '0';
    }
  }

  /**
   * Handle swipe carousel functionality
   */
  handleSwipeCarousel(element, direction) {
    const carousel = element.querySelector('.carousel-inner');
    if (!carousel) return;

    const items = carousel.children;
    const currentIndex = parseInt(carousel.dataset.currentIndex || '0');
    let newIndex = currentIndex;

    if (direction === 'left' && currentIndex < items.length - 1) {
      newIndex = currentIndex + 1;
    } else if (direction === 'right' && currentIndex > 0) {
      newIndex = currentIndex - 1;
    }

    if (newIndex !== currentIndex) {
      carousel.dataset.currentIndex = newIndex;
      carousel.style.transform = `translateX(-${newIndex * 100}%)`;
    }
  }

  /**
   * Setup pull-to-refresh functionality
   */
  setupPullToRefresh() {
    const pullRefreshElements = document.querySelectorAll('.pull-refresh');
    
    pullRefreshElements.forEach(element => {
      let startY = 0;
      let pullDistance = 0;
      const threshold = 100;

      element.addEventListener('touchstart', (e) => {
        startY = e.touches[0].clientY;
      }, { passive: true });

      element.addEventListener('touchmove', (e) => {
        const currentY = e.touches[0].clientY;
        pullDistance = Math.max(0, currentY - startY);

        if (pullDistance > 0 && element.scrollTop === 0) {
          e.preventDefault();
          
          // Update pull indicator
          const indicator = element.querySelector('.pull-indicator');
          if (indicator) {
            const rotation = Math.min(pullDistance * 2, 180);
            indicator.style.transform = `translateY(${pullDistance * 0.5}px) rotate(${rotation}deg)`;
            
            if (pullDistance > threshold) {
              indicator.classList.add('ready');
            } else {
              indicator.classList.remove('ready');
            }
          }
        }
      }, { passive: false });

      element.addEventListener('touchend', () => {
        if (pullDistance > threshold) {
          this.triggerRefresh(element);
        }
        
        // Reset pull state
        const indicator = element.querySelector('.pull-indicator');
        if (indicator) {
          indicator.style.transform = 'translateY(0) rotate(0deg)';
          indicator.classList.remove('ready');
        }
        
        pullDistance = 0;
      }, { passive: true });
    });
  }

  /**
   * Trigger refresh functionality
   */
  async triggerRefresh(element) {
    this.triggerHaptic('medium');
    
    // Add loading state
    element.classList.add('refreshing');
    
    // Dispatch refresh event
    const refreshEvent = new CustomEvent('pullrefresh', {
      detail: { element }
    });
    element.dispatchEvent(refreshEvent);

    // Simulate refresh (replace with actual refresh logic)
    setTimeout(() => {
      element.classList.remove('refreshing');
    }, 2000);
  }

  /**
   * Setup drag and drop functionality
   */
  setupDragDrop() {
    const draggableElements = document.querySelectorAll('.draggable');
    
    draggableElements.forEach(element => {
      let isDragging = false;
      let dragStartX = 0;
      let dragStartY = 0;
      let elementStartX = 0;
      let elementStartY = 0;

      element.addEventListener('touchstart', (e) => {
        isDragging = true;
        const touch = e.touches[0];
        dragStartX = touch.clientX;
        dragStartY = touch.clientY;
        
        const rect = element.getBoundingClientRect();
        elementStartX = rect.left;
        elementStartY = rect.top;
        
        element.classList.add('dragging');
        this.triggerHaptic('light');
      }, { passive: true });

      element.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        
        e.preventDefault();
        const touch = e.touches[0];
        const deltaX = touch.clientX - dragStartX;
        const deltaY = touch.clientY - dragStartY;
        
        element.style.transform = `translate(${deltaX}px, ${deltaY}px)`;
        
        // Check for drop zones
        this.checkDropZones(touch.clientX, touch.clientY, element);
      }, { passive: false });

      element.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        
        isDragging = false;
        element.classList.remove('dragging');
        
        // Check if dropped on valid drop zone
        const dropZone = document.elementFromPoint(
          e.changedTouches[0].clientX,
          e.changedTouches[0].clientY
        )?.closest('.drop-zone');
        
        if (dropZone) {
          this.handleDrop(element, dropZone);
          this.triggerHaptic('strong');
        } else {
          // Reset position
          element.style.transform = 'translate(0, 0)';
        }
      }, { passive: true });
    });
  }

  /**
   * Check for drop zones during drag
   */
  checkDropZones(x, y, draggedElement) {
    const dropZones = document.querySelectorAll('.drop-zone');
    
    dropZones.forEach(zone => {
      const rect = zone.getBoundingClientRect();
      const isOver = x >= rect.left && x <= rect.right && 
                     y >= rect.top && y <= rect.bottom;
      
      if (isOver) {
        zone.classList.add('drag-over');
      } else {
        zone.classList.remove('drag-over');
      }
    });
  }

  /**
   * Handle successful drop
   */
  handleDrop(draggedElement, dropZone) {
    // Remove drag-over state
    dropZone.classList.remove('drag-over');
    
    // Dispatch drop event
    const dropEvent = new CustomEvent('touchdrop', {
      detail: {
        draggedElement,
        dropZone
      }
    });
    dropZone.dispatchEvent(dropEvent);
    
    // Reset dragged element position
    draggedElement.style.transform = 'translate(0, 0)';
  }

  /**
   * Setup haptic feedback
   */
  setupHapticFeedback() {
    // Listen for haptic trigger events
    document.addEventListener('click', (e) => {
      if (e.target.closest('.haptic-light')) {
        this.triggerHaptic('light');
      } else if (e.target.closest('.haptic-medium')) {
        this.triggerHaptic('medium');
      } else if (e.target.closest('.haptic-strong')) {
        this.triggerHaptic('strong');
      }
    });
  }

  /**
   * Trigger haptic feedback
   */
  triggerHaptic(intensity = 'light') {
    if (!this.hapticEnabled) return;
    
    const patterns = {
      light: [10],
      medium: [50],
      strong: [100]
    };
    
    try {
      navigator.vibrate(patterns[intensity] || patterns.light);
    } catch (error) {
      console.warn('Haptic feedback not supported:', error);
    }
  }

  /**
   * Add touch-friendly classes to elements
   */
  enhanceInteractiveElements() {
    // Auto-enhance buttons
    const buttons = document.querySelectorAll('button, .btn, [role="button"]');
    buttons.forEach(btn => {
      if (!btn.classList.contains('touch-enhanced')) {
        btn.classList.add('touch-enhanced');
      }
    });

    // Auto-enhance form inputs
    const inputs = document.querySelectorAll('input, select, textarea');
    inputs.forEach(input => {
      if (!input.classList.contains('touch-enhanced')) {
        input.classList.add('touch-enhanced');
      }
    });

    // Auto-enhance interactive cards
    const interactiveCards = document.querySelectorAll('.card[onclick], .card[data-action]');
    interactiveCards.forEach(card => {
      if (!card.classList.contains('touch-enhanced')) {
        card.classList.add('touch-enhanced');
      }
    });
  }

  /**
   * Optimize scroll performance
   */
  optimizeScrolling() {
    const scrollContainers = document.querySelectorAll('.scroll-container, .table-container');
    
    scrollContainers.forEach(container => {
      // Add momentum scrolling for iOS
      container.style.webkitOverflowScrolling = 'touch';
      
      // Add scroll snap for better UX
      if (container.classList.contains('scroll-snap')) {
        container.style.scrollSnapType = 'x mandatory';
        
        const children = container.children;
        Array.from(children).forEach(child => {
          child.style.scrollSnapAlign = 'start';
        });
      }
    });
  }

  /**
   * Setup responsive touch adjustments
   * Note: Resize handling has been intentionally removed to prevent performance issues.
   * Now uses CSS media queries instead of JavaScript viewport detection for better performance.
   */
  setupResponsiveTouchAdjustments() {
    // Use CSS media queries instead of window.innerWidth for better performance
    // These match our CSS breakpoints exactly
    const isTablet = window.matchMedia('(min-width: 768px) and (max-width: 1024px)').matches;
    const isLargeTouch = window.matchMedia('(min-width: 1025px)').matches && 'ontouchstart' in window;
    
    if (isTablet) {
      document.body.classList.add('touch-tablet');
      console.log('📱 TouchGestureManager: Applied tablet touch styles');
    }
    
    if (isLargeTouch) {
      document.body.classList.add('touch-large');
      console.log('🖥️ TouchGestureManager: Applied large touch styles');
    }    
    
    // Touch adjustments are now applied once on initialization.
    // If responsive touch behavior is needed in the future, it should be
    // coordinated through the centralized ResizeManager utility.
    
    console.log('✅ TouchGestureManager: Responsive touch adjustments applied (resize handling disabled for performance)');
  }

  /**
   * Initialize all touch enhancements
   */
  static init() {
    const manager = new TouchGestureManager();
    
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        manager.enhanceInteractiveElements();
        manager.optimizeScrolling();
        manager.setupResponsiveTouchAdjustments();
      });
    } else {
      manager.enhanceInteractiveElements();
      manager.optimizeScrolling();
      manager.setupResponsiveTouchAdjustments();
    }
    
    return manager;
  }
}

// Export for manual initialization (called by main.js)
export default TouchGestureManager;
